import { useEffect, useRef } from 'react';

import { useAppSelector } from '@/Application/Hooks/Redux';
import useGetTokenUserId from '@/modules/perseo-billing/containers/payment-complete/hooks/use-get-token-user-id';
import { useCmediaPurchaseEvent } from '@/modules/perseo-billing/hooks';
import type { IOrderConfirmation } from '@/modules/perseo-billing/interfaces/src/bff-web/billing';

const getPayloadItems = (order: IOrderConfirmation) => {
  const items = order?.basketSummary?.[0]?.items || [];

  if (!items || items?.length === 0) return [];

  const payloadItems = items
    ?.filter((item) => item?.custom?.fields?.resolvedBidId)
    ?.map((item) => ({
      productId: item?.productKey ?? '',
      quantity: item?.quantity ?? 0,
      unitPrice: item?.prices?.[0]?.price ?? 0
    }));

  return payloadItems;
};

export const useSendCmediaPurchaseEvent = () => {
  const paymentSuccess = useAppSelector(
    (state) => state?.billing?.paymentSuccess
  );

  const { mutate: mutateCmediaPurchaseEvent } = useCmediaPurchaseEvent();
  const tokenUserId = useGetTokenUserId();

  const requestSentRef = useRef(false);

  useEffect(() => {
    if (!paymentSuccess) return;
    if (!mutateCmediaPurchaseEvent) return;
    if (requestSentRef.current) return;
    if (!tokenUserId) return;

    const payloadItems = getPayloadItems(paymentSuccess);

    const hasValidData =
      payloadItems?.length > 0 && paymentSuccess?.id && tokenUserId;

    if (!hasValidData) return;

    const occurredAt = paymentSuccess?.basketSummary?.[0]?.timeStamp;
    const purchasePayload = [
      {
        id: paymentSuccess?.id,
        opaqueUserId: tokenUserId,
        occurredAt,
        items: payloadItems
      }
    ];

    mutateCmediaPurchaseEvent(purchasePayload);
    requestSentRef.current = true;
  }, [
    mutateCmediaPurchaseEvent,
    paymentSuccess,
    paymentSuccess?.basketSummary,
    tokenUserId
  ]);
};
